import { animate, style, transition, trigger } from '@angular/animations'
import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core'
import { Icon, IconComponent } from '../icon'
import { TooltipModule } from '../tooltip'

const hidden = { bottom: '-0.25rem' }
const visible = { bottom: '0' }
const timing = '0.2s ease'

export const TERTIARY_BUTTON_THEMES = ['primary', 'grey'] as const
export type TertiaryButtonTheme = (typeof TERTIARY_BUTTON_THEMES)[number]

/**
 * Button component that appears as plain text, resembling a link. Also used in empty states.
 *
 * @example
 * ``` html
 * <ftr-tertiary-button
 *   text="Tertiary"
 *   iconLeft="ArrowLeft"
 *   theme="primary"
 *   [isLoading]="false"
 *   (onClick)="doSomething()"
 * />
 * ```
 */
@Component({
  selector: 'ftr-tertiary-button',
  templateUrl: './tertiary-button.component.html',
  styleUrl: './tertiary-button.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, IconComponent, TooltipModule],
  animations: [
    trigger('animateLoader', [
      transition(':enter', [style(hidden), animate(timing, style(visible))]),
      transition(':leave', [style(visible), animate(timing, style(hidden))]),
    ]),
  ],
})
export class TertiaryButtonComponent {
  readonly text = input.required<string>()
  readonly iconLeft = input<Icon | undefined>()
  readonly iconRight = input<Icon | undefined>()
  readonly isLoading = input(false)
  readonly testId = input<string>()
  readonly theme = input.required<TertiaryButtonTheme>()
  readonly tooltipMessage = input<string>()

  readonly onClick = output()

  protected readonly shouldShowTooltip = computed(() => Boolean(this.tooltipMessage()))

  protected handleClick(): void {
    if (!this.isLoading()) {
      this.onClick.emit()
    }
  }
}
